/**
 * =============================================================================
 * Página: /afiliado/esqueci-senha
 * Versão: 1.0.0
 *
 * Descrição:
 *   Solicitação de recuperação de senha para afiliados.
 *   Reaproveita 100% o fluxo nativo do Supabase Auth
 *   (`supabase.auth.resetPasswordForEmail`) — não cria nada no backend.
 *
 *   - Visual premium (mesma estrutura split-screen do login).
 *   - Confirmação genérica (não revela se o e-mail existe → boa prática
 *     contra enumeração de usuários).
 *   - Link de retorno para a tela de login.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  affiliateLoginPath,
  affiliateResetPath,
} from '@/lib/affiliate-urls';
import { z } from 'zod';
import {
  Loader2,
  AlertCircle,
  Mail,
  ArrowLeft,
  Sparkles,
  KeyRound,
  CheckCircle2,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { logger } from '@/lib/logger';
import {
  useAffiliateBrandingPublic,
  buildBrandingCssVars,
} from '@/hooks/useAffiliateBranding';

// Validação client-side: e-mail com limite de tamanho
const emailSchema = z
  .string()
  .trim()
  .min(1, 'Informe seu e-mail')
  .email('E-mail inválido')
  .max(255, 'E-mail muito longo');

export default function AffiliateForgotPassword() {
  // tenantSlug presente quando a rota é /afiliado/:tenantSlug/esqueci-senha
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  // Branding personalizado por tenant — mesmo padrão da tela de login.
  const { data: brandingData } = useAffiliateBrandingPublic({
    tenantSlug: tenantSlug ?? null,
  });
  const brandingStyle = useMemo(
    () => buildBrandingCssVars(brandingData?.branding),
    [brandingData],
  );
  const logoUrl = brandingData?.branding?.logo_url ?? null;
  const tenantName = brandingData?.tenant_name ?? null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'E-mail inválido');
      return;
    }

    setSubmitting(true);
    try {
      // Redireciona de volta para a tela de redefinição do afiliado
      // Mantém o tenantSlug para preservar a URL canônica do tenant.
      const redirectTo = `${window.location.origin}${affiliateResetPath(tenantSlug)}`;
      const { error: rpcError } = await supabase.auth.resetPasswordForEmail(
        parsed.data,
        { redirectTo },
      );

      if (rpcError) {
        // Resposta genérica mesmo em alguns erros (evita enumeração).
        logger.warn('AffiliateForgotPassword', 'reset error', rpcError);
      }
      setSent(true);
    } catch (err) {
      logger.error('AffiliateForgotPassword', 'exception', err);
      setError('Não foi possível processar agora. Tente novamente em instantes.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="affiliate-shell min-h-[100dvh] grid md:grid-cols-2 bg-[hsl(var(--aff-bg))]"
      style={brandingStyle}
    >
      {/* Hero — Desktop */}
      <aside className="aff-hero-surface relative hidden md:flex flex-col justify-between overflow-hidden p-10 lg:p-14">
        <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))]">
          {logoUrl ? (
            <div className="flex flex-col gap-1.5">
              <img
                src={logoUrl}
                alt={tenantName ?? ''}
                className="max-h-14 lg:max-h-16 max-w-[220px] object-contain object-left"
              />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] opacity-80">
                Painel do Afiliado
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                <Sparkles className="h-5 w-5" />
              </div>
              <span className="text-sm font-semibold tracking-wide opacity-90">
                {tenantName ? `${tenantName} · Painel do Afiliado` : 'Painel do Afiliado'}
              </span>
            </div>
          )}
        </div>

        <div className="relative z-10 max-w-md space-y-4 text-[hsl(var(--aff-primary-foreground))]">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
            <KeyRound className="h-7 w-7" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
            Recupere
            <br />o acesso
          </h1>
          <p className="text-base lg:text-[17px] leading-relaxed opacity-85">
            Sem estresse. Em poucos cliques você volta a acompanhar suas
            indicações, comissões e saques.
          </p>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-[hsl(var(--aff-primary-foreground))] opacity-75">
          <span className="h-1.5 w-1.5 rounded-full bg-white/70" />
          <span className="text-xs font-medium tracking-wide uppercase">
            Programa de Indicação
          </span>
        </div>
      </aside>

      {/* Mini-hero — Mobile */}
      <div className="aff-hero-surface md:hidden relative h-[28dvh] min-h-[180px] flex items-end overflow-hidden px-6 pb-8">
        <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))] space-y-2">
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={tenantName ?? ''}
                className="max-h-9 max-w-[140px] object-contain object-left"
              />
            ) : (
              <>
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                  <KeyRound className="h-4 w-4" />
                </div>
                <span className="text-[12px] font-semibold tracking-wide opacity-90">
                  Recuperar senha
                </span>
              </>
            )}
          </div>
          <h1 className="text-[24px] font-bold tracking-tight leading-tight">
            Esqueceu sua senha?
          </h1>
        </div>
      </div>

      {/* Coluna do formulário */}
      <main className="flex justify-center px-4 pb-8 md:p-10 lg:p-14 -mt-10 md:mt-0 relative z-10 md:items-center md:justify-center">
        <div className="w-full max-w-md">
          <div
            className="aff-card rounded-3xl p-6 md:p-8 space-y-6 aff-animate-in"
            style={{ boxShadow: 'var(--aff-shadow-hero)' }}
          >
            {!sent ? (
              <>
                <div className="space-y-1.5">
                  <h2 className="text-[22px] md:text-[24px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                    Recuperar senha
                  </h2>
                  <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                    Informe seu e-mail e enviaremos um link para você criar
                    uma nova senha.
                  </p>
                </div>

                {error && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-2xl bg-[hsl(var(--aff-danger)/0.10)] px-3.5 py-2.5 text-[13px] text-[hsl(var(--aff-danger))]"
                  >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span className="leading-snug">{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="email"
                      className="text-[12.5px] font-semibold text-[hsl(var(--aff-foreground))]"
                    >
                      E-mail cadastrado
                    </Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--aff-muted-foreground))]" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="seu@email.com"
                        required
                        autoComplete="email"
                        disabled={submitting}
                        maxLength={255}
                        className="h-11 pl-10 pr-3 rounded-xl bg-[hsl(var(--aff-surface-2))] border-[hsl(var(--aff-border))] focus-visible:ring-[hsl(var(--aff-primary))] text-[hsl(var(--aff-foreground))]"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 rounded-xl text-[hsl(var(--aff-primary-foreground))] hover:opacity-90 border-0 font-semibold text-[15px] shadow-[var(--aff-shadow-soft)] transition-opacity"
                    style={{ background: 'var(--aff-gradient-hero)' }}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      'Enviar link de recuperação'
                    )}
                  </Button>
                </form>
              </>
            ) : (
              // Confirmação genérica (não revela se o e-mail existe)
              <div className="space-y-5 text-center py-2">
                <div
                  className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl text-[hsl(var(--aff-primary-foreground))]"
                  style={{ background: 'var(--aff-gradient-hero)' }}
                >
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <div className="space-y-1.5">
                  <h2 className="text-[22px] font-bold tracking-tight text-[hsl(var(--aff-foreground))]">
                    Verifique seu e-mail
                  </h2>
                  <p className="text-sm text-[hsl(var(--aff-muted-foreground))]">
                    Se o e-mail informado estiver cadastrado, enviamos um
                    link para você redefinir sua senha. Confira também a
                    caixa de spam.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSent(false);
                    setEmail('');
                  }}
                  className="rounded-xl"
                >
                  Enviar para outro e-mail
                </Button>
              </div>
            )}

            {/* Voltar para login */}
            <div className="border-t border-[hsl(var(--aff-border))] pt-4 text-center">
              <Link
                to={affiliateLoginPath(tenantSlug)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--aff-primary))] hover:underline underline-offset-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Voltar para o login
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
